import { z } from "zod";
export const DocumentNumberTypeSchema = z.enum([
  "INVOICE",
  "CREDIT_NOTE",
  "DEBIT_NOTE",
  "DUNNING",
]);
export const NumberPatternSchema = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .superRefine((value, ctx) => {
    if (
      (value.match(/\{0+\}/g) || []).length !== 1 ||
      /[{}]/.test(value.replace(/\{(?:YYYY|YY|MM|0+)\}/g, "")) ||
      /[\u0000-\u001f\u007f]/.test(value)
    )
      ctx.addIssue({
        code: "custom",
        message:
          "Use exactly one sequence placeholder, such as {0000}, and only supported date placeholders.",
      });
  });
export const NumberSequenceSchema = z
  .number()
  .int()
  .min(1)
  .max(Number.MAX_SAFE_INTEGER - 1);
export function formatDocumentNumber(
  pattern: string,
  sequence: number,
  day: string,
): string {
  const format = NumberPatternSchema.parse(pattern),
    counter = NumberSequenceSchema.parse(sequence);
  const date = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .parse(day);
  if (new Date(date + "T00:00:00Z").toISOString().slice(0, 10) !== date)
    throw new Error("Invalid numbering date");
  return format
    .replace(/\{YYYY\}/g, date.slice(0, 4))
    .replace(/\{YY\}/g, date.slice(2, 4))
    .replace(/\{MM\}/g, date.slice(5, 7))
    .replace(/\{0+\}/, (token) =>
      String(counter).padStart(token.length - 2, "0"),
    );
}
export const NumberingRecordSchema = z.object({
  id: z.string().min(1),
  tenantId: z.string().min(1),
  type: DocumentNumberTypeSchema,
  formatString: z.string(),
  currentCounter: z
    .number()
    .int()
    .min(0)
    .max(Number.MAX_SAFE_INTEGER - 1),
  updatedAt: z.string().min(1),
});
export const NumberingRevisionSchema = z
  .string()
  .regex(/^numbering-[a-f0-9]{64}$/);
export const NumberingReviewSchema = z.object({
  tenantId: z.string().min(1),
  type: DocumentNumberTypeSchema,
  asOf: z.string().datetime(),
  configuration: NumberingRecordSchema.nullable(),
  reviewRevision: NumberingRevisionSchema,
  nextNumber: z.string().nullable(),
  numberInUse: z.boolean(),
});
export const NumberingSaveSchema = z
  .object({
    type: DocumentNumberTypeSchema,
    formatString: NumberPatternSchema,
    nextCounter: NumberSequenceSchema,
    reviewRevision: NumberingRevisionSchema,
  })
  .strict();
export const NumberingReceiptSchema = z.object({
  tenantId: z.string().min(1),
  actorId: z.string().min(1),
  attemptKey: z.string().uuid(),
  savedAt: z.string().datetime(),
  configuration: NumberingRecordSchema,
  reviewRevision: NumberingRevisionSchema,
});
export type DocumentNumberType = z.infer<typeof DocumentNumberTypeSchema>;
export type NumberingRecord = z.infer<typeof NumberingRecordSchema>;
export type NumberingReview = z.infer<typeof NumberingReviewSchema>;
export type NumberingSave = z.infer<typeof NumberingSaveSchema>;
export type NumberingReceipt = z.infer<typeof NumberingReceiptSchema>;
